import campaignService from '@/services/campaignService'

const state = {
  campanas: [],
  campanaActual: null,
  loading: false,
  error: null,
}

const getters = {
  campanasActivas: (stateData) => stateData.campanas.filter((c) => c.activo),
  campanasInactivas: (stateData) => stateData.campanas.filter((c) => !c.activo),
  getCampanaById: (stateData) => (id) =>
    stateData.campanas.find((c) => c.id === id),
}

const mutations = {
  SET_CAMPANAS(stateData, campanas) {
    // Transformar métricas para que estén accesibles directamente
    stateData.campanas = campanas.map((campana) => ({
      ...campana,
      enviados: campana.metricas?.total_enviados || 0,
      apertura: campana.metricas?.tasa_apertura || 0,
      clics: campana.metricas?.tasa_clicks ?? null,
      rebote: campana.metricas?.tasa_rebote || 0,
    }))
  },
  SET_CAMPANA_ACTUAL(stateData, campana) {
    // Transformar métricas para que estén accesibles directamente
    stateData.campanaActual = campana
      ? {
          ...campana,
          enviados: campana.metricas?.total_enviados || 0,
          apertura: campana.metricas?.tasa_apertura || 0,
          clics: campana.metricas?.tasa_clicks ?? null,
          rebote: campana.metricas?.tasa_rebote || 0,
        }
      : null
  },
  SET_LOADING(stateData, loading) {
    stateData.loading = loading
  },
  SET_ERROR(stateData, error) {
    stateData.error = error
  },
  ADD_CAMPANA(stateData, campana) {
    // Transformar métricas para que estén accesibles directamente
    const campanaTransformada = {
      ...campana,
      enviados: campana.metricas?.total_enviados || 0,
      apertura: campana.metricas?.tasa_apertura || 0,
      clics: campana.metricas?.tasa_clicks ?? null,
      rebote: campana.metricas?.tasa_rebote || 0,
    }
    stateData.campanas.push(campanaTransformada)
  },
  UPDATE_CAMPANA(stateData, campana) {
    const index = stateData.campanas.findIndex((c) => c.id === campana.id)
    if (index !== -1) {
      // Transformar métricas para que estén accesibles directamente
      const campanaTransformada = {
        ...campana,
        enviados: campana.metricas?.total_enviados || 0,
        apertura: campana.metricas?.tasa_apertura || 0,
        clics: campana.metricas?.tasa_clicks ?? null,
        rebote: campana.metricas?.tasa_rebote || 0,
      }
      stateData.campanas.splice(index, 1, campanaTransformada)
    }
  },
  DELETE_CAMPANA(stateData, id) {
    stateData.campanas = stateData.campanas.filter((c) => c.id !== id)
  },
  CLEAR_ERROR(stateData) {
    stateData.error = null
  },
}

const actions = {
  async fetchCampanas({ commit }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const campanas = await campaignService.obtenerCampanas()
      commit('SET_CAMPANAS', campanas)
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) ||
        error.message ||
        'Error al obtener campañas'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchCampana({ commit }, id) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const campana = await campaignService.obtenerCampana(id)
      commit('SET_CAMPANA_ACTUAL', campana)
      return campana
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) ||
        error.message ||
        'Error al obtener campaña'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async crearCampana({ commit }, datos) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const campana = await campaignService.crearCampana(datos)
      commit('ADD_CAMPANA', campana)
      return campana
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) ||
        error.message ||
        'Error al crear campaña'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async actualizarCampana({ commit }, { id, datos }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const campana = await campaignService.actualizarCampana(id, datos)
      commit('UPDATE_CAMPANA', campana)
      return campana
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) ||
        error.message ||
        'Error al actualizar campaña'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async eliminarCampana({ commit }, id) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      await campaignService.eliminarCampana(id)
      commit('DELETE_CAMPANA', id)
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) ||
        error.message ||
        'Error al eliminar campaña'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async toggleActivo({ commit }, id) {
    commit('CLEAR_ERROR')
    try {
      await campaignService.toggleActivo(id)
      // Refrescar la campaña actualizada
      const campana = await campaignService.obtenerCampana(id)
      commit('UPDATE_CAMPANA', campana)
      return campana
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) ||
        error.message ||
        'Error al cambiar estado de campaña'
      commit('SET_ERROR', errorMessage)
      throw error
    }
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },

  async fetchMetricasDetalladas({ commit }, id) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const metricas = await campaignService.obtenerMetricasDetalladas(id)
      return metricas
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) ||
        error.message ||
        'Error al obtener métricas'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
